import { useReducer } from 'react';

import * as ACTIONS from './actions';
import reducer from './reducer';
import useDropdown from '../usedropdown';
import { AUDIT_TYPES } from '../../../services/contractmanagementservice';

const initialState = {
  isLoading: false,
  loadingMessage: '',
  contractSettingsData: null
};

const useContractManagement = service => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const [priorityWorkByState, priorityWorkByActions] = useDropdown();
  const [priorityStrategyState, priorityStrategyActions] = useDropdown();

  const getAffectedEncounters = (contractId, auditType, itemId) => {
    dispatch(ACTIONS.getAffectedEncountersRequest());

    return service.getAffectedEncounters(contractId, auditType, itemId)
      .then(result => {
        dispatch(ACTIONS.getAffectedEncountersSuccess());
        return Promise.resolve(result);
      })
      .catch(e => {
        dispatch(ACTIONS.getAffectedEncountersFailure(e.toString()));
      });
  };

  const removeAuditReasons = (contractId, auditType, itemId) => {
    dispatch(ACTIONS.removeAuditReasonsRequest());

    return service.removeAuditReasons(contractId, auditType, itemId)
      .then(result => {
        dispatch(ACTIONS.removeAuditReasonsSuccess());
        return Promise.resolve(result);
      })
      .catch(e => {
        dispatch(ACTIONS.removeAuditReasonsFailure(e.toString()));
      });
  };

  const enableClientReview = contractId => {
    dispatch(ACTIONS.enableClientReviewRequest());

    return service.enableClientReview(contractId)
      .then(() => {
        dispatch(ACTIONS.enableClientReviewSuccess());
      })
      .catch(e => dispatch(ACTIONS.enableClientReviewFailure(e.toString())))
  };

  const disableClientReview = contractId => {
    dispatch(ACTIONS.disableClientReviewRequest());

    return service.disableClientReview(contractId)
      .then(() => {
        dispatch(ACTIONS.disableClientReviewSuccess());
      })
      .catch(e => dispatch(ACTIONS.disableClientReviewFailure(e.toString())))
  };

  const enableAutoExport = contractId => {
    dispatch(ACTIONS.enableAutoExportRequest());

    return service.enableAutoExport(contractId)
      .then(result => {
        dispatch(ACTIONS.enableAutoExportSuccess());
      })
      .catch(e => dispatch(ACTIONS.enableAutoExportFailure(e.toString())));
  };

  const disableAutoExport = contractId => {
    dispatch(ACTIONS.disableAutoExportRequest());

    return service.disableAutoExport(contractId)
      .then(result => {
        dispatch(ACTIONS.disableAutoExportSuccess());
      })
      .catch(e => dispatch(ACTIONS.disableAutoExportFailure(e.toString())));
  };

  const enablePurgeDuringNextRead = contractId => {
    dispatch(ACTIONS.enablePurgeDuringNextReadRequest());

    return service.enablePurgeDuringNextRead(contractId)
      .then(result => {
        dispatch(ACTIONS.enablePurgeDuringNextReadSuccess());
      })
      .catch(e => {
        dispatch(ACTIONS.enablePurgeDuringNextReadFailure(e.toString()));
      });
  };

  const disablePurgeDuringNextRead = contractId => {
    dispatch(ACTIONS.disablePurgeDuringNextReadRequest());

    return service.disablePurgeDuringNextRead(contractId)
      .then(result => {
        dispatch(ACTIONS.disablePurgeDuringNextReadSuccess());
      })
      .catch(e => {
        dispatch(ACTIONS.disablePurgeDuringNextReadFailure(e.toString()));
      });
  };

  const enableDocProcAutofinalize = contractId => {
    dispatch(ACTIONS.enableDocProcAutofinalizeRequest());

    return service.enableDocProcAutofinalize(contractId)
      .then(result => {
        dispatch(ACTIONS.enableDocProcAutofinalizeSuccess());
      })
      .catch(e => {
        dispatch(ACTIONS.enableDocProcAutofinalizeFailure(e.toString()));
      });
  };

  const disableDocProcAutofinalize = contractId => {
    dispatch(ACTIONS.disableDocProcAutofinalizeRequest());

    return service.disableDocProcAutofinalize(contractId)
      .then(result => {
        dispatch(ACTIONS.disableDocProcAutofinalizeSuccess());
      })
      .catch(e => {
        dispatch(ACTIONS.disableDocProcAutofinalizeFailure(e.toString()));
      });
  };

  const enableAutofinalizedEncounterDocProcRead = contractId => {
    dispatch(ACTIONS.enableAutofinalizedEncounterDocProcReadRequest());

    return service.enableAutofinalizedEncounterDocProcRead(contractId)
      .then(result => {
        dispatch(ACTIONS.enableAutofinalizedEncounterDocProcReadSuccess());
      })
      .catch(e => {
        dispatch(
          ACTIONS.enableAutofinalizedEncounterDocProcReadFailure(e.toString())
        );
      });
  };

  const disableAutofinalizedEncounterDocProcRead = contractId => {
    dispatch(ACTIONS.disableAutofinalizedEncounterDocProcReadRequest());

    return service.disableAutofinalizedEncounterDocProcRead(contractId)
      .then(result => {
        dispatch(ACTIONS.disableAutofinalizedEncounterDocProcReadSuccess());
      })
      .catch(e => {
        dispatch(
          ACTIONS.disableAutofinalizedEncounterDocProcReadFailure(e.toString())
        );
      });
  };

  const enableNewHCCAudit = contractId => {
    dispatch(ACTIONS.enableNewHCCAuditRequest());

    return service.enableNewHCCAudit(contractId)
      .then(result => {
        dispatch(ACTIONS.enableNewHCCAuditSuccess());
      })
      .catch(e => {
        dispatch(ACTIONS.enableNewHCCAuditFailure(e.toString()));
      });
  };

  const disableNewHCCAudit = (contractId, openDialog) => {
    return getAffectedEncounters(contractId, AUDIT_TYPES.NEW_HCC)
      .then(affectedEncounterData => {
        const {
          contractId,
          affectedEncounterCount,
          auditType,
          itemId
        } = affectedEncounterData;

        dispatch(ACTIONS.disableNewHCCAuditRequest());

        return service.disableNewHCCAudit(contractId)
          .then(() => {
            dispatch(ACTIONS.disableNewHCCAuditSuccess());

            openDialog(
              contractId,
              affectedEncounterCount,
              auditType,
              itemId
            );
          });
      });
  };

  const enableUnvalidatedDxAudit = contractId => {
    dispatch(ACTIONS.enableUnvalidatedDxAuditRequest());

    return service.enableUnvalidatedDxAudit(contractId)
      .then(result => {
        dispatch(ACTIONS.enableUnvalidatedDxAuditSuccess());
      })
      .catch(e => {
        dispatch(ACTIONS.enableUnvalidatedDxAuditFailure(e.toString()));
      });
  };

  const disableUnvalidatedDxAudit = (contractId, openDialog) => {
    return getAffectedEncounters(contractId, AUDIT_TYPES.UNVALIDATED_DXES)
      .then(affectedEncounterData => {
        const {
          contractId,
          affectedEncounterCount,
          auditType,
          itemId
        } = affectedEncounterData;

        dispatch(ACTIONS.disableUnvalidatedDxAuditRequest());

        return service.disableUnvalidatedDxAudit(contractId)
          .then(() => {
            dispatch(ACTIONS.disableUnvalidatedDxAuditSuccess());

            openDialog(
              contractId,
              affectedEncounterCount,
              auditType,
              itemId
            );
          });
      });
  };

  const enableCoderAudit = contractId => {
    dispatch(ACTIONS.enableCoderAuditRequest());

    return service.enableCoderAudit(contractId)
      .then((/* result */) => {
        dispatch(ACTIONS.enableCoderAuditSuccess());
      })
      .catch(e => {
        dispatch(ACTIONS.enableCoderAuditFailure(e.toString()));
      });
  };

  const disableCoderAudit = (contractId, openDialog) => {
    return getAffectedEncounters(contractId, AUDIT_TYPES.CODER)
      .then(affectedEncounterData => {
        const {
          contractId,
          affectedEncounterCount,
          auditType,
          itemId
        } = affectedEncounterData;

        dispatch(ACTIONS.disableCoderAuditRequest());

        return service.disableCoderAudit(contractId)
          .then(() => {
            dispatch(ACTIONS.disableCoderAuditSuccess());

            openDialog(
              contractId,
              affectedEncounterCount,
              auditType,
              itemId
            );
          })
          .catch(e => dispatch(ACTIONS.disableCoderAuditFailure(e.toString())));
      });
  };

  const placeCoderOnAudit = (contractId, coderId, coder) => {
    dispatch(ACTIONS.placeCoderOnAuditRequest());

    return service.placeCoderOnAudit(contractId, coderId)
      .then(() => {
        dispatch(ACTIONS.placeCoderOnAuditSuccess(coder));
      })
      .catch(e => dispatch(ACTIONS.placeCoderOnAuditFailure(e.toString())));
  };

  const removeCoderFromAudit = (contractId, coderId, coder) => {
    dispatch(ACTIONS.removeCoderFromAuditRequest());

    return service.removeCoderFromAudit(contractId, coderId)
      .then(() => {
        dispatch(ACTIONS.removeCoderFromAuditSuccess(coder));
      })
      .catch(e => dispatch(ACTIONS.removeCoderFromAuditFailure(e.toString())));
  };

  const enableCCAudit = contractId => {
    dispatch(ACTIONS.enableCCAuditRequest());

    return service.enableCCAudit(contractId)
      .then(result => {
        dispatch(ACTIONS.enableCCAuditSuccess());
      })
      .catch(e => {
        dispatch(ACTIONS.enableCCAuditFailure(e.toString()));
      });
  };

  const disableCCAudit = (contractId, openDialog) => {
    return getAffectedEncounters(contractId, AUDIT_TYPES.HCC)
      .then(affectedEncounterData => {
        const {
          contractId,
          affectedEncounterCount,
          auditType,
          itemId
        } = affectedEncounterData;

        dispatch(ACTIONS.disableCCAuditRequest());

        return service.disableCCAudit(contractId)
          .then(() => {
            dispatch(ACTIONS.disableCCAuditSuccess());

            openDialog(
              contractId,
              affectedEncounterCount,
              auditType,
              itemId
            );
          })
          .catch(e => dispatch(ACTIONS.disableCCAuditFailure(e.toString())));
      });
  };

  const placeCCOnAudit = (contractId, codeCategoryId, codeCategory) => {
    dispatch(ACTIONS.placeCCOnAuditRequest());

    return service.placeCCOnAudit(contractId, codeCategoryId)
      .then(() => {
        dispatch(ACTIONS.placeCCOnAuditSuccess(codeCategory));
      })
      .catch(e => dispatch(ACTIONS.placeCCOnAuditFailure(e.toString())));
  };

  const removeCCFromAudit = (contractId, codeCategoryId, codeCategory) => {
    dispatch(ACTIONS.removeCCFromAuditRequest());

    return service.removeCCFromAudit(contractId, codeCategoryId)
      .then(() => {
        dispatch(ACTIONS.removeCCFromAuditSuccess(codeCategory));
      })
      .catch(e => dispatch(ACTIONS.removeCCFromAuditFailure(e.toString())));
  };

  const enableDxAudit = contractId => {
    dispatch(ACTIONS.enableDxAuditRequest());

    return service.enableDxAudit(contractId)
      .then((/* result */) => {
        dispatch(ACTIONS.enableDxAuditSuccess());
      })
      .catch(e => {
        dispatch(ACTIONS.enableDxAuditFailure(e.toString()));
      });
  };

  const disableDxAudit = (contractId, openDialog) => {
    return getAffectedEncounters(contractId, AUDIT_TYPES.DX)
      .then(affectedEncounterData => {
        const {
          contractId,
          affectedEncounterCount,
          auditType,
          itemId
        } = affectedEncounterData;

        dispatch(ACTIONS.disableDxAuditRequest());

        return service.disableDxAudit(contractId)
          .then(() => {
            dispatch(ACTIONS.disableDxAuditSuccess());

            openDialog(
              contractId,
              affectedEncounterCount,
              auditType,
              itemId
            );
          })
          .catch(e => dispatch(ACTIONS.disableDxAuditFailure(e.toString())));
      })
  };

  const placeDxOnAudit = (contractId, cmsCodeId, encoding) => {
    dispatch(ACTIONS.placeDxOnAuditRequest());

    return service.placeDxOnAudit(contractId, cmsCodeId)
      .then(() => {
        dispatch(ACTIONS.placeDxOnAuditSuccess(encoding));
      })
      .catch(e => dispatch(ACTIONS.placeDxOnAuditFailure(e.toString())));
  };

  const removeDxFromAudit = (contractId, cmsCodeId, encoding) => {
    dispatch(ACTIONS.removeDxFromAuditRequest());

    return service.removeDxFromAudit(contractId, cmsCodeId)
      .then(() => {
        dispatch(ACTIONS.removeDxFromAuditSuccess(encoding));
      })
      .catch(e => dispatch(ACTIONS.removeDxFromAuditFailure(e.toString())));
  };

  const enableCCCNewDxAudit = contractId => {
    dispatch(ACTIONS.enableCCCNewDxAuditRequest());

    return service.enableCCCNewDxAudit(contractId)
      .then(() => {
        dispatch(ACTIONS.enableCCCNewDxAuditSuccess());
      })
      .catch(e => dispatch(ACTIONS.enableCCCNewDxAuditFailure(e.toString())));
  };

  const disableCCCNewDxAudit = (contractId, openDialog) => {
    return getAffectedEncounters(contractId, AUDIT_TYPES.CCC_NEW_DX)
      .then(affectedEncounterData => {
        const {
          contractId,
          affectedEncounterCount,
          auditType,
          itemId
        } = affectedEncounterData;

        dispatch(ACTIONS.disableCCCNewDxAuditRequest());

        return service.disableCCCNewDxAudit(contractId)
          .then(() => {
            dispatch(ACTIONS.disableCCCNewDxAuditSuccess());

            openDialog(
              contractId,
              affectedEncounterCount,
              auditType,
              itemId
            );
          })
          .catch(e => dispatch(ACTIONS.disableCCCNewDxAuditFailure(e.toString())));
      })
  };

  const enableCCCUnvalidatedDxAudit = contractId => {
    dispatch(ACTIONS.enableCCCUnvalidatedDxAuditRequest());

    return service.enableCCCUnvalidatedDxAudit(contractId)
      .then(() => {
        dispatch(ACTIONS.enableCCCUnvalidatedDxAuditSuccess());
      })
      .catch(e => ACTIONS.enableCCCUnvalidatedDxAuditFailure(e.toString()));
  };

  const disableCCCUnvalidatedDxAudit = (contractId, openDialog) => {
    return getAffectedEncounters(contractId, AUDIT_TYPES.DX)
      .then(affectedEncounterData => {
        const {
          contractId,
          affectedEncounterCount,
          auditType,
          itemId
        } = affectedEncounterData;

        dispatch(ACTIONS.disableCCCUnvalidatedDxAuditRequest());

        return service.disableCCCUnvalidatedDxAudit(contractId)
          .then(() => {
            dispatch(ACTIONS.disableCCCUnvalidatedDxAuditSuccess());

            openDialog(
              contractId,
              affectedEncounterCount,
              auditType,
              itemId
            );
          })
          .catch(e => dispatch(ACTIONS.disableCCCUnvalidatedDxAuditFailure(e.toString())));
      });
  };

  const manualAutofinalize = (contractId, options) => {
    dispatch(ACTIONS.manualAutofinalizeRequest());

    return service.manualAutofinalize(contractId, options)
      .then(() => {
        dispatch(ACTIONS.manualAutofinalizeSuccess(options));
      })
      .catch(e => dispatch(ACTIONS.manualAutofinalizeFailure(e.toString())));
  };

  const purgeEncounters = contractId => {
    dispatch(ACTIONS.purgeEncountersRequest());

    return service.purgeEncounters(contractId)
      .then(() => {
        dispatch(ACTIONS.purgeEncountersSuccess());
      })
      .catch(e => dispatch(ACTIONS.purgeEncountersFailure(e.toString())));
  };

  const changeWorkByPriority = (contractSetting, event) => {
    priorityWorkByActions.setSelectedItem(event);

    dispatch(ACTIONS.changePriorityWorkByRequest());
    return service.changePrioritySetting(contractSetting)
      .then(result => {
        dispatch(ACTIONS.changePriorityWorkBySuccess(result))
      })
      .catch(e => dispatch(ACTIONS.changePriorityWorkByFailure(e.toString())));
  };

  const changeStrategyPriority = (contractSetting, event) => {
    priorityStrategyActions.setSelectedItem(event);

    dispatch(ACTIONS.changePriorityStrategyRequest());
        return service.changePrioritySetting(contractSetting)
          .then(result => {
            dispatch(ACTIONS.changePriorityStrategySuccess(result))
          })
          .catch(e => dispatch(ACTIONS.changePriorityStrategyFailure(e.toString())));
  };

  const enableEncounterAutoReread = contractId => {
    dispatch(ACTIONS.enableAutoRereadRequest());

    return service.enableAutoReread(contractId)
      .then(result => {
        dispatch(ACTIONS.enableAutoRereadSuccess());
      })
      .catch(e => {
        dispatch(ACTIONS.enableAutoRereadFailure(e.toString()));
      });
  };

  const disableEncounterAutoReread = contractId => {
    dispatch(ACTIONS.disableAutoRereadRequest());

    return service.disableAutoReread(contractId)
      .then(result => {
        dispatch(ACTIONS.disableAutoRereadSuccess());
      })
      .catch(e => {
        dispatch(ACTIONS.disableAutoRereadFailure(e.toString()));
      });
  };

  const hydrate = data => {
    priorityStrategyActions.setItems(data.priorityStrategyOptions);
    priorityWorkByActions.setItems(data.priorityWorkByOptions);

    dispatch(ACTIONS.hydrate(data));
  };

  const boundActions = {
    getAffectedEncounters,
    removeAuditReasons,
    enableCCAudit,
    disableCCAudit,
    placeCCOnAudit,
    removeCCFromAudit,
    enableDxAudit,
    disableDxAudit,
    placeDxOnAudit,
    removeDxFromAudit,
    enableCoderAudit,
    disableCoderAudit,
    placeCoderOnAudit,
    removeCoderFromAudit,
    enableNewHCCAudit,
    disableNewHCCAudit,
    enableAutoExport,
    disableAutoExport,
    enablePurgeDuringNextRead,
    disablePurgeDuringNextRead,
    enableDocProcAutofinalize,
    disableDocProcAutofinalize,
    enableUnvalidatedDxAudit,
    disableUnvalidatedDxAudit,
    enableCCCNewDxAudit,
    disableCCCNewDxAudit,
    enableCCCUnvalidatedDxAudit,
    disableCCCUnvalidatedDxAudit,
    enableAutofinalizedEncounterDocProcRead,
    disableAutofinalizedEncounterDocProcRead,
    manualAutofinalize,
    purgeEncounters,
    changeWorkByPriority,
    changeStrategyPriority,
    enableEncounterAutoReread,
    disableEncounterAutoReread,
    hydrate,
    priorityWorkByActions,
    priorityStrategyActions,
    enableClientReview,
    disableClientReview
  };

  return [{ ...state, priorityWorkByState, priorityStrategyState }, boundActions];
};

export default useContractManagement;
